import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Plus, Sparkle, CalendarBlank, Broadcast } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const TONE = {
  new: "border-[#002FA7] bg-[#EEF3FF] text-[#002FA7]",
  shortlisted: "border-[#0F7A3D] bg-[#EAF7EF] text-[#0B5C2E]",
  audition: "border-[#B3241B] bg-[#FFF6E5] text-[#8E1C15]",
  booked: "border-[#0F172A] bg-[#0F172A] text-white",
  rejected: "border-border bg-slate-100 text-slate-500",
};

export const CastingStrip = ({ onOpen }) => {
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({});

  const load = useCallback(() => {
    api.get("/casting/applications", { params: { limit: 8 } })
      .then(({ data }) => { setApps(data.applications); setCounts(data.counts); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  if (apps.length === 0) return null;

  return (
    <div className="border border-border bg-white p-4" data-testid="casting-strip">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
          <Broadcast size={15} weight="fill" className="text-[#0F7A3D]" /> Casting — live applications
        </p>
        <button data-testid="casting-strip-open" onClick={onOpen}
          className="tap-safe text-xs font-semibold text-[#002FA7] hover:underline">
          Open the casting board ({counts.new || 0} new)
        </button>
      </div>
      <div className="scroll-x mt-3 flex gap-2 overflow-x-auto">
        {apps.map((a, i) => (
          <div key={a.id} data-testid={`casting-strip-app-${i}`}
            className="w-56 shrink-0 border border-border bg-slate-50 p-3">
            <p className="truncate font-semibold text-[#0F172A]">{a.name}</p>
            <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
              {a.role_label} · {[a.city, a.size].filter(Boolean).join(" · ")}
            </p>
            <span className={`mt-2 inline-block border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${TONE[a.status] || TONE.new}`}>
              {a.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CastingBoard = ({ segments = [] }) => {
  const { user, token } = useAuth();
  const [meta, setMeta] = useState(null);
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [slot, setSlot] = useState({});
  const [call, setCall] = useState({ title: "", role_type: "model", segment: "lingerie",
    brief: "", city: "", country: "", pay_note: "", closing_date: "", public: true });
  const latest = useRef(null);

  const loadCalls = useCallback(() => {
    api.get("/casting/calls").then(({ data }) => setMeta(data)).catch(oops);
  }, []);

  const loadApps = useCallback((quiet = false) => {
    api.get("/casting/applications", { params: filter ? { status: filter } : {} })
      .then(({ data }) => {
        if (!quiet && data.applications[0]) latest.current = data.applications[0].created_at;
        if (quiet && data.applications[0] && data.applications[0].created_at !== latest.current) {
          latest.current = data.applications[0].created_at;
          toast.success(`New application — ${data.applications[0].name}`);
        }
        setApps(data.applications);
        setCounts(data.counts);
      })
      .catch(() => {});
  }, [filter]);

  useEffect(() => { loadCalls(); }, [loadCalls]);
  useEffect(() => { loadApps(); }, [loadApps]);
  useEffect(() => {
    const id = setInterval(() => loadApps(true), 20000);
    return () => clearInterval(id);
  }, [loadApps]);

  const openCall = async () => {
    setBusy(true);
    try {
      await api.post("/casting/calls", { ...call, closing_date: call.closing_date || null });
      toast.success("Casting call is open — share the link");
      setShowNew(false);
      setCall({ ...call, title: "", brief: "" });
      loadCalls();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const decide = async (id, status, postToFeed = false) => {
    try {
      await api.post(`/casting/applications/${id}/status`, {
        status, audition_at: slot[id] || null, post_to_feed: postToFeed });
      toast.success(postToFeed ? `Marked ${status} and posted to the live feed` : `Marked ${status}`);
      loadApps();
    } catch (e) { oops(e); }
  };

  const aiNote = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/casting/applications/${id}/ai-note`);
      toast.success(`Read by ${data.engine_label}`);
      loadApps();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!meta) return <p className="py-8 text-center text-sm text-slate-500">Loading the casting board…</p>;
  const segOptions = segments.length ? segments : meta.segments;

  return (
    <div className="space-y-5" data-testid="casting-board">
      <div className="flex flex-wrap items-center gap-2">
        {user?.role === "admin" && (
          <Button variant="outline" className="w-full sm:w-auto" data-testid="casting-new-toggle"
            onClick={() => setShowNew((v) => !v)}>
            <Plus size={16} className="mr-1.5" /> {showNew ? "Close the form" : "Open a casting call"}
          </Button>
        )}
        <span className="inline-flex items-center gap-1.5 border border-[#0F7A3D] bg-[#EAF7EF] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#0B5C2E]"
          data-testid="casting-live-badge">
          <Broadcast size={13} weight="fill" /> Live · new applications appear on their own
        </span>
      </div>

      {showNew && (
        <div className="border border-border bg-white p-4" data-testid="casting-new-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>What you are casting for</Label>
              <Input data-testid="casting-call-title" value={call.title} placeholder="Workplace lingerie campaign — autumn shoot"
                onChange={(e) => setCall({ ...call, title: e.target.value })} /></div>
            <div><Label>Role</Label>
              <select data-testid="casting-call-role" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={call.role_type} onChange={(e) => setCall({ ...call, role_type: e.target.value })}>
                {meta.role_types.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select></div>
            <div><Label>Segment</Label>
              <select data-testid="casting-call-segment" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={call.segment} onChange={(e) => setCall({ ...call, segment: e.target.value })}>
                {segOptions.map((s) => <option key={s} value={s}>{LABEL(s)}</option>)}
              </select></div>
            <div><Label>City</Label><Input data-testid="casting-call-city" value={call.city} onChange={(e) => setCall({ ...call, city: e.target.value })} /></div>
            <div><Label>Country</Label><Input data-testid="casting-call-country" value={call.country} onChange={(e) => setCall({ ...call, country: e.target.value })} /></div>
            <div><Label>Pay / fee note</Label><Input data-testid="casting-call-pay" value={call.pay_note} placeholder="Day rate GBP 350 + usage" onChange={(e) => setCall({ ...call, pay_note: e.target.value })} /></div>
            <div><Label>Applications close</Label><Input data-testid="casting-call-closing" type="date" value={call.closing_date} onChange={(e) => setCall({ ...call, closing_date: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>The brief</Label>
              <Textarea data-testid="casting-call-brief" rows={3} value={call.brief}
                onChange={(e) => setCall({ ...call, brief: e.target.value })} /></div>
          </div>
          <label className="tap-safe mt-3 flex items-center gap-2 text-sm text-slate-600">
            <input data-testid="casting-call-public" type="checkbox" className="h-5 w-5 shrink-0"
              checked={call.public} onChange={(e) => setCall({ ...call, public: e.target.checked })} />
            Public and shareable — anyone with the link can apply
          </label>
          <Button className="mt-3 w-full sm:w-auto" data-testid="casting-call-save" onClick={openCall}
            disabled={busy || call.title.trim().length < 3}>
            {busy ? "Opening…" : "Open the casting call"}
          </Button>
        </div>
      )}

      {meta.calls.length === 0 ? (
        <EmptyState testid="casting-calls-empty" title="No casting calls yet"
          description="Open one for a shoot or a brand ambassador search, share the link, and applications land here as they arrive." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="casting-calls">
          {meta.calls.map((c, i) => (
            <div key={c.id} data-testid={`casting-call-${i}`} className="border border-border bg-white p-4">
              <p className="font-semibold text-[#0F172A]">{c.title}</p>
              <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                {c.role_label} · {LABEL(c.segment)} · {c.applications} application(s) · {c.status}
              </p>
              {c.brief && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{c.brief}</p>}
              {c.public && c.token && (
                <>
                  <a data-testid={`casting-call-link-${i}`} href={`/casting/${c.token}`} target="_blank" rel="noreferrer"
                    className="mt-2 inline-block text-xs font-semibold text-[#002FA7] hover:underline">
                    Open the public apply page
                  </a>
                  <div className="mt-2">
                    <ShareBar url={`${BACKEND}/api/share/casting/${c.token}`} text={`${c.role_label} casting — ${c.title}`}
                      testid={`casting-call-share-${i}`} compact instagram />
                  </div>
                </>
              )}
              {user?.role === "admin" && c.status === "open" && (
                <button data-testid={`casting-call-close-${i}`}
                  onClick={() => api.post(`/casting/calls/${c.id}/close`).then(loadCalls).catch(oops)}
                  className="tap-safe mt-3 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#B3241B] hover:text-[#B3241B]">
                  Close it
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="scroll-x flex gap-1.5 overflow-x-auto" data-testid="casting-status-filter">
        {[["", "Everyone"], ...meta.statuses.map((s) => [s, LABEL(s)])].map(([key, label]) => (
          <button key={key || "all"} data-testid={`casting-filter-${key || "all"}`} onClick={() => setFilter(key)}
            className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
              filter === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
            {label}{key && counts[key] != null ? ` (${counts[key]})` : ""}
          </button>
        ))}
      </div>

      {apps.length === 0 ? (
        <EmptyState testid="casting-apps-empty" title="No applications yet"
          description="Share the casting link on Facebook or Instagram — anyone who applies shows up here within seconds." />
      ) : (
        <div className="space-y-3" data-testid="casting-applications">
          {apps.map((a, i) => (
            <div key={a.id} data-testid={`casting-app-${i}`} className="border border-border bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-[#0F172A]">{a.name}</p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                    {a.role_label} · {LABEL(a.segment)} · {(a.created_at || "").slice(0, 16).replace("T", " ")}
                  </p>
                </div>
                <span data-testid={`casting-app-status-${i}`}
                  className={`border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONE[a.status] || TONE.new}`}>
                  {a.status}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-700">
                {[a.email, a.phone, [a.city, a.country].filter(Boolean).join(", "),
                  a.height_cm ? `${a.height_cm} cm` : null, a.size ? `size ${a.size}` : null]
                  .filter(Boolean).join(" · ")}
              </p>
              {(a.portfolio_url || a.instagram) && (
                <p className="mt-1 text-xs text-slate-600">
                  {a.instagram && <span>{a.instagram}</span>}
                  {a.portfolio_url && (
                    <a href={a.portfolio_url} target="_blank" rel="noreferrer"
                      data-testid={`casting-app-portfolio-${i}`} className="ml-2 font-semibold text-[#002FA7] hover:underline">
                      portfolio
                    </a>
                  )}
                </p>
              )}
              {a.availability && <p className="mt-1 text-xs text-slate-600">Free: {a.availability}</p>}
              {a.about && <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{a.about}</p>}
              {a.photo_ids.length > 0 && (
                <div className="scroll-x mt-3 flex gap-2 overflow-x-auto" data-testid={`casting-app-photos-${i}`}>
                  {a.photo_ids.map((pid) => (
                    <img key={pid} src={`${API}/images/${pid}?auth=${token}`} alt={a.name}
                      className="h-28 w-24 shrink-0 border border-border object-cover" />
                  ))}
                </div>
              )}
              {a.ai_note && (
                <p data-testid={`casting-app-ai-${i}`} className="mt-3 whitespace-pre-line border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-sm text-[#0F172A]">
                  {a.ai_note}
                </p>
              )}
              {a.audition_at && (
                <p className="mt-2 text-xs font-semibold text-[#8E1C15]">Audition {a.audition_at}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                <button data-testid={`casting-shortlist-${i}`} onClick={() => decide(a.id, "shortlisted", true)}
                  className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F7A3D] hover:text-[#0B5C2E]">
                  Shortlist
                </button>
                <button data-testid={`casting-book-${i}`} onClick={() => decide(a.id, "booked", true)}
                  className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]">
                  Booked
                </button>
                <button data-testid={`casting-reject-${i}`} onClick={() => decide(a.id, "rejected")}
                  className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#B3241B] hover:text-[#B3241B]">
                  Not this time
                </button>
                <button data-testid={`casting-ai-${i}`} onClick={() => aiNote(a.id)} disabled={busy}
                  className="tap-safe inline-flex items-center gap-1.5 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60">
                  <Sparkle size={13} /> {busy ? "Reading…" : "AI fit note"}
                </button>
                <div className="flex items-center gap-1.5">
                  <CalendarBlank size={14} className="text-slate-400" />
                  <Input data-testid={`casting-slot-${i}`} type="datetime-local" className="w-[13.5rem]"
                    value={slot[a.id] || ""} onChange={(e) => setSlot({ ...slot, [a.id]: e.target.value })} />
                  <button data-testid={`casting-offer-audition-${i}`} onClick={() => decide(a.id, "audition", true)}
                    disabled={!slot[a.id]}
                    className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#B3241B] hover:text-[#8E1C15] disabled:opacity-60">
                    Offer audition
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="flex items-center gap-1.5 text-[11px] text-slate-500">
        <Camera size={13} /> Applicant details and photos stay inside the team — they are never shown on the public pages.
      </p>
    </div>
  );
};
